import { useState } from 'react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowRight, Check, Loader2, Sparkles, UserCircle, X } from 'lucide-react';

// After script analysis: "this new character looks like one you already have" — reuse it or dismiss.
export function CharacterMatchBanner({ projectId, characters, onChange, onChooseOther }) {
  const [busy, setBusy] = useState(null);
  const pending = characters.filter(c => c.suggested_match);
  if (pending.length === 0) return null;

  const link = async (c) => {
    setBusy(c.id);
    try {
      const res = await api.post(`/projects/${projectId}/characters/${c.id}/link-existing`, { source_id: c.suggested_match.id });
      onChange(res.data);
      toast.success(`${c.name} now uses ${c.suggested_match.name} from ${c.suggested_match.project_name || 'another project'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not link character'); }
    finally { setBusy(null); }
  };
  const dismiss = async (c) => {
    setBusy(c.id);
    try {
      await api.delete(`/projects/${projectId}/characters/${c.id}/suggested-match`);
      const { suggested_match, ...rest } = c;
      onChange(rest);
    } catch { toast.error('Could not dismiss'); }
    finally { setBusy(null); }
  };
  const linkAll = async () => {
    setBusy('all');
    for (const c of pending) {
      try {
        const res = await api.post(`/projects/${projectId}/characters/${c.id}/link-existing`, { source_id: c.suggested_match.id });
        onChange(res.data);
      } catch { toast.error(`Could not link ${c.name}`); }
    }
    setBusy(null);
    toast.success(`Reused ${pending.length} existing character${pending.length > 1 ? 's' : ''}`);
  };

  const Thumb = ({ path, testId }) => (
    <div className="w-10 h-10 rounded-lg bg-[#0a0a0e] border border-white/[0.08] overflow-hidden shrink-0 flex items-center justify-center" data-testid={testId}>
      {path ? <img src={getImageUrl(path)} alt="" className="w-full h-full object-cover" /> : <UserCircle className="w-5 h-5 text-[#55555C]" />}
    </div>
  );

  return (
    <div className="mb-5 p-4 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.05]" data-testid="character-match-banner">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-semibold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#00F0FF]" /> {pending.length} character{pending.length > 1 ? 's' : ''} may already exist in your workspace</p>
          <p className="text-xs text-[#8A8A93] mt-0.5">Reuse the existing look, voice and tags so the same person stays consistent across projects.</p>
        </div>
        {pending.length > 1 && (
          <button data-testid="match-use-all" onClick={linkAll} disabled={!!busy} className="ff-btn-secondary text-xs flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50">
            {busy === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Use all
          </button>
        )}
      </div>
      <div className="space-y-2">
        {pending.map(c => (
          <div key={c.id} data-testid={`match-row-${c.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 p-2.5 rounded-lg bg-black/30 border border-white/[0.05]">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Thumb path={c.image_storage_path} />
              <div className="min-w-0">
                <p className="text-xs text-white font-medium truncate">{c.name}</p>
                <p className="text-[10px] text-[#55555C]">New in this project</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[#00F0FF] shrink-0 mx-1" />
              <Thumb path={c.suggested_match.image_storage_path} testId={`match-thumb-${c.id}`} />
              <div className="min-w-0">
                <p className="text-xs text-white font-medium truncate flex items-center gap-1.5">{c.suggested_match.name}
                  {c.suggested_match.method === 'ai' && <span className="text-[9px] px-1.5 py-px rounded-full bg-[#A78BFA]/10 text-[#C4B5FD] border border-[#A78BFA]/30" title={c.suggested_match.reason || ''}>AI match{c.suggested_match.confidence ? ` · ${Math.round(c.suggested_match.confidence * 100)}%` : ''}</span>}
                </p>
                <p className="text-[10px] text-[#8A8A93] truncate max-w-[260px]" title={c.suggested_match.reason || ''}>{c.suggested_match.project_name || 'Another project'}{c.suggested_match.reason ? ` · ${c.suggested_match.reason}` : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`match-use-${c.id}`} onClick={() => link(c)} disabled={!!busy}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/25 disabled:opacity-50 flex items-center gap-1.5">
                {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Use existing
              </button>
              {onChooseOther && (
                <button data-testid={`match-other-${c.id}`} onClick={() => onChooseOther(c)} disabled={!!busy} title="Pick a different existing character"
                  className="px-2.5 py-1.5 rounded-lg text-xs text-[#8A8A93] hover:text-white border border-white/[0.08] disabled:opacity-50">Other…</button>
              )}
              <button data-testid={`match-dismiss-${c.id}`} onClick={() => dismiss(c)} disabled={!!busy} title="Keep as a new character"
                className="p-1.5 rounded-lg text-[#8A8A93] hover:text-white hover:bg-white/10 disabled:opacity-50"><X className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
